import React, {FC, useEffect, useState} from "react";
import Container from "@material-ui/core/Container";
import {
    Box, Button, Breadcrumbs,
    Card, CircularProgress,
    Grid, makeStyles, Typography, useMediaQuery,
} from "@material-ui/core";
import Navigation from "../../../Components/Navigation";
import {colorInputBorderGrayish} from "../../../Constants/theme";
import RecordListItem from "../../../Components/ListItems/RecordListItem/RecordListItem";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {AllergiesIllnessesForm} from "../../../Components";
import moment from "moment";
import {useTheme} from "@material-ui/core/styles";
import {editAnimal} from "../../../redux/pets/pets.actions";
import {useSnackbar} from "notistack";
import {conditionTypes as contentSwitch} from "../../../Constants/conditionTypes";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {Link} from "react-router-dom";

const useStyle = makeStyles((theme) => ({
    itemDivider: {
        borderBottom: `1px solid ${colorInputBorderGrayish}`
    },
    button: {
        padding: "10px 33px",
        [theme.breakpoints.down("sm")]: {
            marginTop: "15px"
        },
    },
    buttonContainer: {
        marginTop: 30,
        marginBottom: 30,
        [theme.breakpoints.down("sm")]: {
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
        },
    },
}));

const fields = {
    ALLERGY: "allergies",
    CHRONIC_CONDITION: "chronicConditions",
    PREVENTATIVES: "medications",
    MEDICATIONS: "medications",
};

const emptyList = {
    ALLERGY: "No known allergies",
    CHRONIC_CONDITION: "No issues recorded",
    PREVENTATIVES: "No preventatives recorded",
    MEDICATIONS: "No medications recorded",
};

const MedicalConditions: FC = () => {
    const [loaderShowed, setLoaderShowed] = useState(false);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [contentType, setContentType] = useState("ALLERGY");
    const pet = useSelector((state: RootState) => state?.pets.selectedPet);
    const classes = useStyle();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const {enqueueSnackbar} = useSnackbar();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isNavMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        setLoaderShowed(true);
        setTimeout(() => {
            setLoaderShowed(false);
        }, 1000);
    }, []);

    const renderList = () => {
        const fieldName = fields[contentType];
        let conditionsList = [...pet[fieldName]];
        switch (contentType) {
            case "PREVENTATIVES":
                conditionsList = conditionsList.filter(({isPreventative}) => isPreventative);
                break;
            case "MEDICATIONS":
                conditionsList = conditionsList.filter(({isPreventative}) => !isPreventative);
                break;
        }
        return (
            conditionsList?.length ?
                conditionsList
                    .sort((a, b) => moment(b.date).diff(moment(a.date)))
                    .map(({id, name, date}, idx) => {
                        const dateStarted = date ? moment(date).format("MMMM YYYY") : "Date not specified";
                        return <RecordListItem
                            key={idx}
                            id={name}
                            title={name}
                            date={dateStarted}
                            setSelectedRecord={setSelectedRecord}
                            setOpenModal={setOpenEditForm}
                            styles={(idx === conditionsList.length - 1) ? "" : classes.itemDivider}
                        />;
                    })
                :
                <Typography variant="h6" color="textSecondary">
                    {emptyList[contentType]}
                </Typography>
        );
    };

    const submitPetCondition = async (condition) => {
        const fieldName = fields[contentType];
        let data = pet[fieldName];
        if (selectedRecord) {
            data = data.filter(item => item.name !== selectedRecord);
        }
        const fieldToEdit = {
            [fieldName]: [
                ...data,
                {...condition}
            ]
        };
        try {
            await dispatch(editAnimal(pet.id, {
                ownerId: pet.ownerId,
                ...fieldToEdit
            }));
        } catch (err) {
            enqueueSnackbar(err.errorMessage, {
                variant: "error",
                autoHideDuration: 3000
            });
        } finally {
            setSelectedRecord(null);
        }
    };

    const deletePetCondition = async (condition) => {
        const fieldName = fields[contentType];
        let data = pet[fieldName];
        if (selectedRecord) {
            data = data.filter(item => item.name !== selectedRecord);
        }
        const fieldToEdit = {
            [fieldName]: data,
        };
        try {
            await dispatch(editAnimal(pet.id, {
                ownerId: pet.ownerId,
                ...fieldToEdit
            }));
        } catch (err) {
            enqueueSnackbar(err.errorMessage, {
                variant: "error",
                autoHideDuration: 3000
            });
        } finally {
            setSelectedRecord(null);
        }
    };

    return (
        <Container maxWidth="md">
            <Box marginTop="115px" marginBottom="75px">
                <Grid container>
                    {!isNavMobile &&
                    <Grid item md={3} xs={12}>
                        <Navigation/>
                    </Grid>
                    }
                    <Grid item md={9} xs={12}>
                        <Card
                            variant="outlined"
                            style={{padding: 25}}
                        >
                            <Box marginBottom="30px">
                                <Grid container alignItems="center">
                                        <Box marginBottom="55px">
                                            <Breadcrumbs separator={<NavigateNextIcon fontSize="large"/>} aria-label="breadcrumb">
                                                <Link color="inherit" to={`/pets/${pet.id}`}  style={{textDecoration: "none"}}>
                                                    <Typography variant="h2">{pet.name}</Typography>
                                                </Link>
                                                <Typography variant="h2" style={{wordBreak: "break-word"}}>Health Summary</Typography>
                                            </Breadcrumbs>
                                        </Box>
                                    <Grid item xs={3} container justify={isMobile ? "flex-start" : "flex-end"}>
                                        <Button
                                            className={classes.button}
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                setOpenEditForm(true);
                                            }}
                                        >
                                            Add New
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box className={classes.buttonContainer}>
                                <Grid container justify="flex-start" spacing={1}>
                                    {
                                        contentSwitch.map((item, idx) => {
                                            if (item.value === contentType) {
                                                return <Grid item sm={6} xs={12} md={3} key={idx}>
                                                    <Button
                                                        onClick={setContentType.bind(null, item.value)}
                                                        color="secondary"
                                                        variant="contained"
                                                        fullWidth
                                                    >
                                                        {item.label}
                                                    </Button>
                                                </Grid>;
                                            }
                                            return <Grid item sm={6} xs={12} md={3} key={idx}>
                                                <Button
                                                    onClick={setContentType.bind(null, item.value)}
                                                    variant="contained"
                                                    fullWidth
                                                >
                                                    {item.label}
                                                </Button>
                                            </Grid>;
                                        })
                                    }
                                </Grid>
                            </Box>
                            {loaderShowed ?
                                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                    <CircularProgress/>
                                </Box>
                                :
                                renderList()
                            }
                            <AllergiesIllnessesForm
                                isOpen={openEditForm}
                                type={contentType}
                                setOpenDialog={setOpenEditForm}
                                handleSubmit={submitPetCondition}
                                itemToEdit={selectedRecord}
                                setItemToEdit={setSelectedRecord}
                                handleDelete={deletePetCondition}
                            />
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default MedicalConditions;
